/**
 * UpstreamProvenanceCard — renders the source-type → source-detail tree
 * for the Lineage lens.  Driven by the persisted `brief.provenance` field
 * which carries source_type, source_persona_id, source_review_id, source
 * URLs, and (Phase 1) source_asset_id for from-ad backfilled briefs.
 */

import { useNavigate } from 'react-router-dom';
import { ExternalLink, FileText } from 'lucide-react';
import type { GenerationBrief } from '@/services/api';

const SOURCE_TYPE_META: Record<
  string,
  { icon: string; label: string; description: string }
> = {
  persona_enriched: {
    icon: '🧠',
    label: 'Persona-enriched',
    description: 'Generated from a persona’s decision architecture.',
  },
  screenshot_extraction: {
    icon: '📸',
    label: 'Screenshot extraction',
    description: 'Pulled from a screenshot of an existing brief or doc.',
  },
  freeform_input: {
    icon: '✍️',
    label: 'Freeform input',
    description: 'Synthesized from a freeform prompt the user supplied.',
  },
  asset_backfill: {
    icon: '📦',
    label: 'Asset back-fill',
    description: 'Reverse-engineered from an existing ad creative.',
  },
  creative_review: {
    icon: '🔍',
    label: 'Creative review backfill',
    description: 'Distilled from a creative review session.',
  },
  competitor_analysis: {
    icon: '🎯',
    label: 'Competitor analysis',
    description: 'Generated from competitor teardown materials.',
  },
  template_clone: {
    icon: '📋',
    label: 'Template clone',
    description: 'Started from a saved brief template.',
  },
  ad_pipeline: {
    icon: '⚡',
    label: 'Ad pipeline',
    description: 'Created automatically by the ad-generation pipeline.',
  },
  session_context: {
    icon: '💬',
    label: 'Session context',
    description: 'Built from accumulated session insights.',
  },
  manual: {
    icon: '✏️',
    label: 'Manual',
    description: 'Composed manually in the workbench.',
  },
};

interface Props {
  brief: GenerationBrief;
}

export function UpstreamProvenanceCard({ brief }: Props) {
  const navigate = useNavigate();
  const provenance = brief.provenance;

  const sourceType = provenance?.source_type || 'manual';
  const meta = SOURCE_TYPE_META[sourceType] ?? SOURCE_TYPE_META.manual;

  const sourcePersonaId = (provenance as { source_persona_id?: string })?.source_persona_id;
  const sourceReviewId = (provenance as { source_review_id?: string })?.source_review_id;
  const sourceUrl = (provenance as { source_url?: string })?.source_url;
  const sourceAssetId = (provenance as { source_asset_id?: string })?.source_asset_id;
  const sourceMaterials = (provenance as { source_materials?: Array<Record<string, unknown>> })
    ?.source_materials;

  const parentBriefId = brief.parent_brief_id;
  const sectionsCarriedForward = (
    (brief.metadata as { sections_carried_forward?: string[] } | undefined)
      ?.sections_carried_forward ?? []
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
      <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
        <FileText size={14} className="text-slate-400" />
        Upstream provenance
      </h3>

      {/* Primary source */}
      <Branch icon={meta.icon} title={meta.label} subtitle={meta.description}>
        {sourcePersonaId && (
          <DetailLine
            label="From persona"
            value={sourcePersonaId.replace(/_/g, ' ')}
          />
        )}
        {sourceReviewId && (
          <DetailLine
            label="From creative review"
            value={sourceReviewId}
          />
        )}
        {sourceAssetId && (
          <DetailLine
            label="From ad asset"
            value={sourceAssetId}
          />
        )}
        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-sky-600 hover:underline"
          >
            <ExternalLink size={10} />
            {sourceUrl}
          </a>
        )}
      </Branch>

      {/* Attached materials */}
      {sourceMaterials && sourceMaterials.length > 0 && (
        <Branch
          icon="📎"
          title={`Source materials (${sourceMaterials.length})`}
          subtitle="Reference files attached during creation."
        >
          <ul className="text-[11px] text-slate-700 space-y-0.5">
            {sourceMaterials.slice(0, 5).map((m, i) => {
              const filename =
                typeof m.filename === 'string'
                  ? m.filename
                  : typeof m.label === 'string'
                    ? m.label
                    : 'attachment';
              return <li key={i}>• {filename}</li>;
            })}
            {sourceMaterials.length > 5 && (
              <li className="italic text-slate-400">
                and {sourceMaterials.length - 5} more
              </li>
            )}
          </ul>
        </Branch>
      )}

      {/* Parent brief (versioning) */}
      {parentBriefId && (
        <Branch
          icon="📺"
          title="Parent brief"
          subtitle="This brief was forked from a prior version."
        >
          <button
            type="button"
            onClick={() => navigate(`/creative/briefs/${parentBriefId}`)}
            className="text-[11px] font-mono text-sky-600 hover:underline"
          >
            {parentBriefId}
          </button>
          {sectionsCarriedForward.length > 0 && (
            <div className="text-[11px] text-slate-600 mt-1">
              Sections carried forward:{' '}
              {sectionsCarriedForward.map((s) => s.replace(/_/g, ' ')).join(', ')}
            </div>
          )}
        </Branch>
      )}

      {!sourcePersonaId &&
        !sourceReviewId &&
        !sourceAssetId &&
        !sourceUrl &&
        !parentBriefId &&
        (!sourceMaterials || sourceMaterials.length === 0) && (
          <p className="text-[11px] text-slate-400">
            No upstream sources recorded for this brief.
          </p>
        )}
    </div>
  );
}

function Branch({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="text-base leading-none w-6 shrink-0 pt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800">{title}</div>
        {subtitle && (
          <div className="text-[11px] text-slate-500 leading-snug">{subtitle}</div>
        )}
        {children && <div className="mt-1.5 space-y-1">{children}</div>}
      </div>
    </div>
  );
}

function DetailLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-[11px] text-slate-700">
      <span className="text-slate-400 font-mono uppercase tracking-wide mr-1.5">
        {label}
      </span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
